import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
  apiErrorHandler,
  getBackendErrorMessage,
} from "@/core/api/api-error-handler";
import {
  invalidateAfterMutation,
  payrollBatchCacheKeys,
} from "@/core/api/invalidate-after-mutation";
import { mutateIdempotent } from "@/core/api/mutate-idempotent";
import { logger } from "@/core/utils/func/logger";
import { payrollEndpoints } from "@/features/payroll/api/endpoints";
import type { PayrollBatchLaunchResponseDto } from "@/features/payroll/api/dto";

/**
 * Lance une paie SIMULATED (`POST /PayrollBatches/{id}/launch`). Exige une
 * simulation récente (sinon 422 « relancez-la »), au moins une ligne payable
 * et un solde suffisant constaté à la simulation. wallet-service revérifie le
 * solde pour la paie ENTIÈRE avant tout débit : insuffisant → la paie revient
 * `SIMULATED` sans rien débiter (422, motif explicite). Sinon : création d'une
 * transaction `PAYROLL_CREDIT` et d'une réservation par ligne, un code de
 * retrait par salarié non-membre, puis UNE écriture comptable agrégée sous la
 * référence `FPY-…`.
 *
 * Idempotency-Key OBLIGATOIRE (guide §4.7, v4.1) : la clé est persistée par
 * empreinte du corps (même tentative après coupure réseau / reload) ; un 409
 * attend puis rejoue la même clé ; un 422 oublie la clé (lancement ultérieur
 * = nouvelle clé). **Rejouer avec la même clé ne débite jamais deux fois.**
 */
export const useLaunchPayrollBatch = (batchId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["payroll", "payrollBatches", "launch", batchId],
    mutationFn: () =>
      mutateIdempotent<PayrollBatchLaunchResponseDto>({
        scopeId: `payroll.payrollBatches.launch.${batchId}`,
        url: payrollEndpoints.payrollBatches.launch(batchId),
        body: {},
      }),
    onSuccess: () =>
      invalidateAfterMutation(queryClient, payrollBatchCacheKeys(batchId)),
    onError: (error) => {
      const message = getBackendErrorMessage(error) ?? apiErrorHandler(error);
      logger.error(
        `useLaunchPayrollBatch failed: ${message}`,
        error instanceof Error ? error : undefined,
        { prefix: "payroll" }
      );
    },
  });
};
